import React, { useState } from 'react'
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import moment from 'moment';
import { z } from 'zod';
import { Cancel } from "@mui/icons-material";
import { X } from "lucide-react";

import { useResourceHook, useLoadCurrentResource } from "../../hooks/resourcehooks";
import { InventorySchema, ResourceType, TrainingInventorySchema, ResourceInventorySchema, InventoryCatalogSuggestion } from '../../data/types';
import { MeasurementUnits, InventoryCategories } from "../../utils/enums";
import { PriceRecommendationNote } from '../../components/PriceRecommendationNote';
import { InventoryProductImageInput } from '../../components/InventoryProductImageInput';
import { 
  getProductPriceRecommendationDetail, 
  // getUsagePriceRecommendationDetail, getUsageTrackProfile, UsageMeasurementUnit,
  // getSpaceAllocationDescription,
  // getUsageMeasureUnitOptions,
  // SPACE_ALLOCATION_OPTIONS,
  // type SpaceAllocation,
 } from '../../utils/helpers/productPricing';
import { searchInventoryCatalog } from '../../utils/helpers/inventoryCatalogSearch';
// import { ReactComponent as RightArrow } from "../../icons/ScrollRightArrow.svg";
// import { ReactComponent as LeftArrow } from "../../icons/ScrollLeftArrow.svg";
import { capitalizeString, getChangedAttributes, getInventoryStockQuantityFieldLabel, INVENTORY_ITEM_DESCRIPTION_MAX_LENGTH } from '../../utils/helpers';

import { AddInventorySchema, AddTrainingInventorySchema } from "../../utils/validationSchemas";

import type { FieldValues, UseFormRegister } from 'react-hook-form';
import { toast } from 'sonner';

const INVENTORY_SUGGESTION_DEBOUNCE_MS = 150;

function useInventoryNameSuggestions(watchedCategory: string) {
  const [suggestions, setSuggestions] = useState<InventoryCatalogSuggestion[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState<InventoryCatalogSuggestion | null>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>();

  React.useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    []
  );

  const runSearch = React.useCallback(
    (query: string) => {
      const trimmed = query.trim();
      if (!trimmed || !watchedCategory) {
        setSuggestions([]);
        return;
      }
      setSuggestions(searchInventoryCatalog(watchedCategory, trimmed));
    },
    [watchedCategory]
  );

  const handleItemNameChange = React.useCallback(
    (query: string) => {
      setSelectedSuggestion(null);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      const trimmed = query.trim();
      if (!trimmed) {
        setSuggestions([]);
        return;
      }

      debounceRef.current = setTimeout(() => {
        runSearch(trimmed);
      }, INVENTORY_SUGGESTION_DEBOUNCE_MS);
    },
    [runSearch]
  );

  const handleCategoryChange = React.useCallback(
    (itemName: string) => {
      setSelectedSuggestion(null);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      runSearch(itemName);
    },
    [runSearch]
  );

  const clearSuggestions = React.useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setSuggestions([]);
    setSelectedSuggestion(null);
  }, []);

  const selectSuggestion = React.useCallback((suggestion: InventoryCatalogSuggestion) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setSelectedSuggestion(suggestion);
    setSuggestions([]);
  }, []);

  return {
    suggestions,
    selectedSuggestion,
    handleItemNameChange,
    handleCategoryChange,
    clearSuggestions,
    selectSuggestion,
  };
}

type AddProductFormProps = {
  isModal?: boolean
  isOpen?: boolean
  resourceCategory: string
  resourceId?: string
  resourceType: ResourceType
  setCurrentInventoryList: React.Dispatch<React.SetStateAction<InventorySchema[]>> | React.Dispatch<React.SetStateAction<TrainingInventorySchema[]>>
  setIsAddingProduct: React.Dispatch<React.SetStateAction<boolean>>
}

type UpdateProductFormProps = {
  isModal?: boolean
  itemId: string
  resourceCategory: string
  resourceId?: string
  resourceType: ResourceType
  setCurrentInventoryList: React.Dispatch<React.SetStateAction<InventorySchema[]>> | React.Dispatch<React.SetStateAction<TrainingInventorySchema[]>>
  setIsAddingProduct: React.Dispatch<React.SetStateAction<boolean>>
}

// --- Training Form ---
const AddTrainingProductForm: React.FC<AddProductFormProps> = ({ resourceCategory, resourceId, isModal, setIsAddingProduct, resourceType, setCurrentInventoryList }) => {
  const form = useForm<z.infer<typeof AddTrainingInventorySchema>>({
    resolver: zodResolver(AddTrainingInventorySchema),
    defaultValues: {
      item_name: "Schedule",
      item_description: "",
      category: "schedule",
      unit_price: 0,
      total_quantity: 1,
      measure_unit: "ea",
      date: "",
      times: [],
      duration: 1,
    },
  });
  const { register, handleSubmit, setValue, getValues, watch, formState } = form;
  const { addInventoryItem, isError } = useResourceHook({ resourceId });
  const [currentTime, setCurrentTime] = useState<string>("");
  const { errors } = formState;
  
  // Get today's date in YYYY-MM-DD format for date input min attribute
  const today = moment().format('YYYY-MM-DD');
  const selectedDate = watch("date");
  
  // Calculate minimum time: if date is today, use current time (rounded up to next 30 min interval), otherwise "00:00"
  const getMinTime = () => {
    if (selectedDate === today) {
      const now = moment();
      const minutes = now.minute();
      // Round up to next 30-minute interval (step="1800" = 30 minutes)
      let roundedMinutes: number;
      let roundedHour = now.hour();
      
      if (minutes === 0) {
        // Already on the hour, use current time
        roundedMinutes = 0;
      } else if (minutes <= 30) {
        // Round up to :30
        roundedMinutes = 30;
      } else {
        // Round up to next hour (:00)
        roundedMinutes = 0;
        roundedHour = now.hour() + 1;
        if (roundedHour >= 24) {
          // If past midnight, allow any time tomorrow
          return "00:00";
        }
      }
      
      return moment().hour(roundedHour).minute(roundedMinutes).second(0).format('HH:mm');
    }
    return "00:00";
  };

  const handleAddTime = () => {
    const times = getValues('times') || [];
    if (currentTime && Array.isArray(times) && !times.includes(currentTime)) {
      setValue('times', [...times, currentTime]);
      setCurrentTime("");
    }
  };

  const handleRemoveTime = (timeToRemove: string) => {
    const times = getValues('times') || [];
    if (Array.isArray(times)) {
      const updatedTimes = times.filter((time: string) => time !== timeToRemove);
      setValue('times', updatedTimes);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "times" && value) {
      const selectedTime = moment(value, 'HH:mm');
      const minute = selectedTime.minute();
      if (minute % 30 === 0) {
        setCurrentTime(selectedTime.format('HH:mm'));
      } else {
        const roundedTime = selectedTime.minute(minute < 30 ? 0 : 30).format('HH:mm');
        setCurrentTime(roundedTime);
      }
    }
  };

  const onSubmit = (data: FieldValues) => {
    // console.log("Form submitted with data:", data);
    // console.log("Form errors:", errors);
    // Generate item_name from date if not provided or if it's just the default
    let itemName = data.item_name;
    if (!itemName || itemName === "Schedule") {
      if (data.date) {
        itemName = `Schedule - ${moment(data.date).format('MMM DD, YYYY')}`;
      } else {
        itemName = "Schedule";
      }
    }
    
    const scheduleData = { 
      ...data, 
      item_name: itemName,
      resource_type: resourceType 
    } as TrainingInventorySchema;
    
    // console.log("Schedule data to add:", scheduleData);
    
    // If resourceId exists, add via API (for editing existing resource)
    if (resourceId) {
      // console.log("Adding schedule to resource:", resourceId);
      // console.log("Schedule data:", scheduleData);
      addInventoryItem(resourceId, scheduleData);
      if(isError){
        toast.error("Failed to add schedule. Please try again.");
        return;
      } else {
        toast.success("Schedule added successfully!");
      }
    } else {
      // If no resourceId, add to local state (for new resource being created)
      if (setCurrentInventoryList) {
        setCurrentInventoryList((prev: any) => {
          const updated = [...prev, scheduleData];
          // console.log("Updated inventory list:", updated);
          return updated;
        });
        toast.success("Schedule added successfully!");
        // Reset form after successful submission
        form.reset();
        setCurrentTime("");
      } else {
        console.error("setCurrentInventoryList is not provided");
        toast.error("Failed to add schedule. Please try again.");
        return;
      }
    }
    setIsAddingProduct(false);
  };

  const onError = (errors: any) => {
    console.error("Form validation errors:", errors);
    const errorMessages = Object.keys(errors).map(key => {
      return `${key}: ${errors[key]?.message || 'Invalid'}`;
    }).join(', ');
    toast.error(`Please fix the following errors: ${errorMessages}`);
  };

  return (
    <>
      {!isModal && <button className='border-0 font-raleway hover:text-primary-200 text-primary-300 mr-auto' onClick={() => setIsAddingProduct(false)}>Back</button>}
      <p className='text-black text-[18px] font-bold font-raleway my-1'>Add {resourceCategory} Schedule</p>
      <p className='text-[#4D4D4D] text-[16px] font-light font-open-sans my-1'>Fill out the details below to add your culinary training resource.</p>

      <div className="flex flex-col my-4 w-full">
        <p className='font-raleway text-black text-[16px] font-medium'>Add Date</p>
        <input
          type='date'
          {...register("date")}
          min={today}
          className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px] font-raleway text-[#808080]'
        />
        {errors.date && (
          <span className="text-red-500 text-sm font-open-sans">{errors.date.message as string}</span>
        )}
      </div>

      <div className="flex flex-col my-4 w-full">
        <p className='font-raleway text-black text-[16px] font-medium'>Add Time</p>
        <div className='flex flex-row justify-between'>
          <input
            type='time'
            name="times"
            step="1800"
            min={getMinTime()}
            max="23:30"
            value={currentTime}
            onChange={handleInputChange}
            className='border-[#E9E9E9] border-[1px] w-10/12 rounded-md h-[50px] text-[16px] font-raleway text-[#808080]'
          />
          <button type="button" onClick={handleAddTime} className='rounded-full p-2 bg-primary-200 text-white h-10 w-10 m-1 font-bold'>+</button>
        </div>
        {errors.times && (
          <span className="text-red-500 text-sm font-open-sans">{errors.times.message as string}</span>
        )}
        {Array.isArray(watch("times")) && watch("times").length > 0 && (
          <div className='flex flex-row font-open-sans border-t-[1px] border-b-[1px] border-[#E9E9E9] h-[70px] my-4 w-11/12 '>
            {watch("times").map((time: string, i: number) => (
              <div key={i} className='flex flex-col text-center my-auto mr-6 relative'>
                {/* <b>Option {i + 1}</b> */}
                <div className="flex flex-row items-center justify-center">
                  <span className="p-1 bg-primary-300 rounded-md text-white my-1 flex flex-row items-center gap-1">
                    {moment(time, 'HH:mm').format('hh:mm a')}
                    <button
                      type="button"
                      onClick={() => handleRemoveTime(time)}
                      className="ml-1 text-white hover:text-gray-200 cursor-pointer flex items-center justify-center"
                      aria-label="Remove time"
                    >
                      <Cancel className="h-3 w-3" />
                    </button>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className='flex flex-col my-4 w-full'>
        <span className="font-raleway text-black text-[16px] font-medium">Duration (hours)</span>
        <input
          {...register("duration", { valueAsNumber: true })}
          type="number"
          className="border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px] font-raleway text-[#808080]"
        />
        {errors.duration && (
          <span className="text-red-500 text-sm font-open-sans">{errors.duration.message as string}</span>
        )}
      </div>

      <div className='flex flex-col my-4 w-full'>
        <span className="font-raleway text-black text-[16px] font-medium">Capacity</span>
        <input
          {...register("total_quantity", { valueAsNumber: true })}
          type="number"
          className="border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px] font-raleway text-[#808080]"
        />
        {errors.total_quantity && (
          <span className="text-red-500 text-sm font-open-sans">{errors.total_quantity.message as string}</span>
        )}
      </div>

      <div className='flex flex-row text-[16px] justify-between w-11/12 mx-auto mb-4 border-t-[1px] border-[#E9E9E9] py-2'>
        <button 
          type="button"
          className='border-0 font-raleway w-[160px] text-white bg-primary-200 p-2 rounded-md ml-auto' 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(onSubmit, onError)(e);
          }}
        >
          Add Schedule
        </button>
      </div>
    </>
  );
};


const UpdateTrainingProductForm: React.FC<UpdateProductFormProps> = ({ resourceCategory, resourceId, isModal, setIsAddingProduct, resourceType, setCurrentInventoryList, itemId }) => {
  const { currentResource } = useLoadCurrentResource(resourceId || "");
  const [originalItem, setOriginalItem] = useState<TrainingInventorySchema | null>(null);
  const [currentTime, setCurrentTime] = useState<string>("");
  
  const form = useForm<z.infer<typeof AddTrainingInventorySchema>>({
    resolver: zodResolver(AddTrainingInventorySchema),
    defaultValues: {
      item_name: "Schedule",
      item_description: "",
      category: "schedule",
      unit_price: 0,
      total_quantity: 1,
      measure_unit: "ea",
      date: "",
      times: [],
      duration: 1,
    },
  });
  const { register, handleSubmit, setValue, getValues, watch, formState, reset } = form;
  const { updateInventoryItemById } = useResourceHook({ resourceId });
  const { errors } = formState;
  
  // Get today's date in YYYY-MM-DD format for date input min attribute
  const today = moment().format('YYYY-MM-DD');
  const selectedDate = watch("date");
  
  // Calculate minimum time: if date is today, use current time (rounded up to next 30 min interval), otherwise "00:00"
  const getMinTime = () => {
    if (selectedDate === today) {
      const now = moment();
      const minutes = now.minute();
      // Round up to next 30-minute interval (step="1800" = 30 minutes)
      let roundedMinutes: number;
      let roundedHour = now.hour();
      
      if (minutes === 0) {
        // Already on the hour, use current time
        roundedMinutes = 0;
      } else if (minutes <= 30) {
        // Round up to :30
        roundedMinutes = 30;
      } else {
        // Round up to next hour (:00)
        roundedMinutes = 0;
        roundedHour = now.hour() + 1;
        if (roundedHour >= 24) {
          // If past midnight, allow any time tomorrow
          return "00:00";
        }
      }
      
      return moment().hour(roundedHour).minute(roundedMinutes).second(0).format('HH:mm');
    }
    return "00:00";
  };

  // Find the existing item from resource inventory
  React.useEffect(() => {
    if (currentResource && itemId && (!originalItem || originalItem.id !== itemId)) {
      const resourceInventory = currentResource?.resource_inventory as ResourceInventorySchema;
      const schedules = resourceInventory?.schedule as TrainingInventorySchema[];
      const existingItem = schedules?.find(item => item.id === itemId);
      if (existingItem) {
        setOriginalItem(existingItem);
        // Populate form with existing data
        reset({
          item_name: existingItem.item_name || "Schedule",
          item_description: existingItem.item_description || "",
          category: existingItem.category || "schedule",
          unit_price: existingItem.unit_price || 0,
          total_quantity: existingItem.total_quantity || 1,
          measure_unit: existingItem.measure_unit || "ea",
          date: existingItem.date || "",
          times: existingItem.times || [],
          duration: existingItem.duration || 1,
        });
        if (existingItem.times && existingItem.times.length > 0) {
          setCurrentTime("");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentResource, itemId]); // Removed reset and originalItem from dependencies to prevent infinite loops

  const handleAddTime = () => {
    const times = getValues('times') || [];
    if (currentTime && Array.isArray(times) && !times.includes(currentTime)) {
      setValue('times', [...times, currentTime]);
      setCurrentTime("");
    }
  };

  const handleRemoveTime = (timeToRemove: string) => {
    const times = getValues('times') || [];
    if (Array.isArray(times)) {
      const updatedTimes = times.filter((time: string) => time !== timeToRemove);
      setValue('times', updatedTimes);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "times" && value) {
      const selectedTime = moment(value, 'HH:mm');
      const minute = selectedTime.minute();
      if (minute % 30 === 0) {
        setCurrentTime(selectedTime.format('HH:mm'));
      } else {
        const roundedTime = selectedTime.minute(minute < 30 ? 0 : 30).format('HH:mm');
        setCurrentTime(roundedTime);
      }
    }
  };

  const onSubmit = async (data: FieldValues) => {
    // Generate item_name from date if not provided or if it's just the default
    let itemName = data.item_name;
    if (!itemName || itemName === "Schedule") {
      if (data.date) {
        itemName = `Schedule - ${moment(data.date).format('MMM DD, YYYY')}`;
      } else {
        itemName = "Schedule";
      }
    }
    
    const scheduleData = { 
      ...data, 
      item_name: itemName,
      resource_type: resourceType 
    } as TrainingInventorySchema;
    
    // If resourceId and itemId exist, update via API
    if (resourceId && itemId && originalItem) {
      // Use getChangedAttributes to only send changed fields
      const changedAttributes = getChangedAttributes(originalItem, scheduleData);
      
      if (Object.keys(changedAttributes).length > 0) {
        try {
          const result = await updateInventoryItemById(resourceId, itemId, changedAttributes as TrainingInventorySchema);
          if (result) {
            toast.success("Schedule updated successfully!");
            setIsAddingProduct(false);
          } else {
            toast.error("Failed to update schedule. Please try again.");
          }
        } catch (error) {
          console.error("Error updating schedule:", error);
          toast.error("Failed to update schedule. Please try again.");
        }
      } else {
        toast.info("No changes detected.");
        setIsAddingProduct(false);
      }
    } else {
      toast.error("Missing required information to update schedule.");
    }
  };

  const onError = (errors: any) => {
    console.error("Form validation errors:", errors);
    const errorMessages = Object.keys(errors).map(key => {
      return `${key}: ${errors[key]?.message || 'Invalid'}`;
    }).join(', ');
    toast.error(`Please fix the following errors: ${errorMessages}`);
  };

  return (
    <>
      {!isModal && <button className='border-0 font-raleway hover:text-primary-200 text-primary-300 mr-auto' onClick={() => setIsAddingProduct(false)}>Back</button>}
      <p className='text-black text-[18px] font-bold font-raleway my-1'>Update {resourceCategory} Schedule</p>
      <p className='text-[#4D4D4D] text-[16px] font-light font-open-sans my-1'>Update the details below for your culinary training resource.</p>

      <div className="flex flex-col my-4 w-full">
        <p className='font-raleway text-black text-[16px] font-medium'>Add Date</p>
        <input
          type='date'
          {...register("date")}
          min={today}
          className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px] font-raleway text-[#808080]'
        />
        {errors.date && (
          <span className="text-red-500 text-sm font-open-sans">{errors.date.message as string}</span>
        )}
      </div>

      <div className="flex flex-col my-4 w-full">
        <p className='font-raleway text-black text-[16px] font-medium'>Add Time</p>
        <div className='flex flex-row justify-between'>
          <input
            type='time'
            name="times"
            step="1800"
            min={getMinTime()}
            max="23:30"
            value={currentTime}
            onChange={handleInputChange}
            className='border-[#E9E9E9] border-[1px] w-10/12 rounded-md h-[50px] text-[16px] font-raleway text-[#808080]'
          />
          <button type="button" onClick={handleAddTime} className='rounded-full p-2 bg-primary-200 text-white h-10 w-10 m-1 font-bold'>+</button>
        </div>
        {errors.times && (
          <span className="text-red-500 text-sm font-open-sans">{errors.times.message as string}</span>
        )}
        {Array.isArray(watch("times")) && watch("times").length > 0 && (
          <div className='flex flex-row font-open-sans border-t-[1px] border-b-[1px] border-[#E9E9E9] h-[70px] my-4 w-11/12 '>
            {watch("times").map((time: string, i: number) => (
              <div key={i} className='flex flex-col text-center my-auto mr-6 relative'>
                {/* <b>Option {i + 1}</b> */}
                <div className="flex flex-row items-center justify-center">
                  <span className="p-1 px-2 bg-primary-300 rounded-md text-white my-1 flex flex-row items-center gap-1">
                    {moment(time, 'HH:mm').format('hh:mm a')}
                    <button
                      type="button"
                      onClick={() => handleRemoveTime(time)}
                      className=" text-white hover:text-gray-200 cursor-pointer flex items-center justify-center"
                      aria-label="Remove time"
                    >
                      <Cancel className="h-3 w-3" />
                    </button>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className='flex flex-col my-4 w-full'>
        <span className="font-raleway text-black text-[16px] font-medium">Duration (hours)</span>
        <input
          {...register("duration", { valueAsNumber: true })}
          type="number"
          className="border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px] font-raleway text-[#808080]"
        />
        {errors.duration && (
          <span className="text-red-500 text-sm font-open-sans">{errors.duration.message as string}</span>
        )}
      </div>

      <div className='flex flex-col my-4 w-full'>
        <span className="font-raleway text-black text-[16px] font-medium">Capacity</span>
        <input
          {...register("total_quantity", { valueAsNumber: true })}
          type="number"
          className="border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px] font-raleway text-[#808080]"
        />
        {errors.total_quantity && (
          <span className="text-red-500 text-sm font-open-sans">{errors.total_quantity.message as string}</span>
        )}
      </div>

      <div className='flex flex-row text-[16px] justify-between w-11/12 mx-auto mb-4 border-t-[1px] border-[#E9E9E9] py-2'>
        <button 
          type="button"
          className='border-0 font-raleway w-[160px] text-white bg-primary-200 p-2 rounded-md ml-auto' 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(onSubmit, onError)(e);
          }}
        >
          Update Schedule
        </button>
      </div>
    </>
  );
};

// --- Inventory Form ---
type InventoryFormFieldProps = {
  label: string;
  htmlFor?: string;
  error?: string;
  children: React.ReactNode;
};

const InventoryFormField: React.FC<InventoryFormFieldProps> = ({ label, htmlFor, error, children }) => (
  <div className="inventory-form-field">
    <label htmlFor={htmlFor} className="inventory-field-label">
      {label}
    </label>
    {children}
    {error && <p className="inventory-field-error">{error}</p>}
  </div>
);

type InventoryProductFormHeaderProps = {
  isEdit: boolean;
  isModal?: boolean;
  onClose?: () => void;
  resourceType: ResourceType;
};

const InventoryProductFormHeader: React.FC<InventoryProductFormHeaderProps> = ({
  isEdit,
  isModal = false,
  onClose,
  resourceType,
}) => (
  <div
    className={`inventory-header-divider${isModal ? " inventory-header-divider-modal" : ""}`}
    data-testid="inventory-product-form-header"
  >
    <div className={isModal ? "flex items-start justify-between gap-3" : undefined}>
      <div className="min-w-0 flex-1">
        <p className="inventory-header-title">{isEdit ? `Update ${capitalizeString(resourceType)} Inventory` : `Add ${capitalizeString(resourceType)} Inventory`}</p>
        <p className="inventory-header-subtitle">
          {isEdit
            ? `Edit inventory details for this ${resourceType}`
            : `Create new inventory item for this ${resourceType}`}
        </p>
      </div>
      {isModal && onClose && (
        <button
          type="button"
          onClick={onClose}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#e6e6e6] text-primary/50 hover:bg-gray-100"
          aria-label="Close"
          data-testid="inventory-product-form-close"
        >
          <X className="h-5 w-5" />
        </button>
      )}
    </div>
  </div>
);

type InventoryProductFormFieldsProps = {
  register: UseFormRegister<any>;
  errors: Record<string, { message?: string } | undefined>;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onItemNameChange: (query: string) => void;
  onCategoryChange: () => void;
  suggestions: InventoryCatalogSuggestion[];
  onSelectSuggestion: (suggestion: InventoryCatalogSuggestion) => void;
  selectedProductImage: string;
  setSelectedProductImage: React.Dispatch<React.SetStateAction<string>>;
  onClearImage: () => void;
  productRecommendation: ReturnType<typeof getProductPriceRecommendationDetail>;
  // usageRecommendation: ReturnType<typeof getUsagePriceRecommendationDetail> | null;
  watchedCategory: string;
  watchedMeasureUnit: string;
  selectedSuggestion: InventoryCatalogSuggestion | null;
  isReusable: boolean;
  onIsReusableChange: (value: boolean) => void;
  trackUsage: boolean;
  onTrackUsageChange: (value: boolean) => void;
  // usageMeasureUnit: string;
  // onUsageMeasureUnitChange: (unit: UsageMeasurementUnit) => void;
  // spaceAllocation: SpaceAllocation;
  // onSpaceAllocationChange: (value: SpaceAllocation) => void;
  watchedItemDescription?: string;
};

const InventoryProductFormFields: React.FC<InventoryProductFormFieldsProps> = ({
  register,
  errors,
  handleInputChange,
  onItemNameChange,
  onCategoryChange,
  suggestions,
  onSelectSuggestion,
  selectedProductImage,
  setSelectedProductImage,
  onClearImage,
  productRecommendation,
  // usageRecommendation,
  watchedCategory,
  watchedMeasureUnit,
  selectedSuggestion,
  isReusable,
  onIsReusableChange,
  trackUsage,
  onTrackUsageChange,
  // usageMeasureUnit,
  // onUsageMeasureUnitChange,
  // spaceAllocation,
  // onSpaceAllocationChange,
  watchedItemDescription,
}) => {
  const categoryLabel =
    (watchedCategory && InventoryCategories[watchedCategory as keyof typeof InventoryCategories]) ||
    (watchedCategory ? capitalizeString(watchedCategory) : "Inventory");

  const stockQuantityLabel = getInventoryStockQuantityFieldLabel(watchedMeasureUnit);
  const showSuggestions = suggestions.length > 0;
  const itemNameField = register("item_name");
  const categoryField = register("category");
  const suggestionsListId = React.useId();
  // const usageProfile = getUsageTrackProfile(watchedCategory || "");
  // const usageUnitOptions = getUsageMeasureUnitOptions(watchedCategory || "");

  return (
    <div className="inventory-form-fields" data-testid="inventory-product-form-fields">
    <InventoryFormField label="Item category" error={errors.category?.message as string | undefined}>
      <select
        {...categoryField}
        onChange={(event) => {
          void categoryField.onChange(event);
          onCategoryChange();
        }}
        className="inventory-select"
      >
        <option value="" disabled>
          -- Category --
        </option>
        {Object.entries(InventoryCategories).map(([categoryKey, categoryOptionLabel]) => (
          <option key={categoryKey} value={categoryKey}>
            {categoryOptionLabel}
          </option>
        ))}
      </select>
    </InventoryFormField>

    <InventoryFormField label="Item name" htmlFor="item_name" error={errors.item_name?.message as string | undefined}>
      <div className="relative">
        <input
          id="item_name"
          type="text"
          {...itemNameField}
          onChange={(event) => {
            void itemNameField.onChange(event);
            onItemNameChange(event.target.value);
          }}
          placeholder="Enter item name"
          className="inventory-input"
          role="combobox"
          aria-expanded={showSuggestions}
          aria-controls={suggestionsListId}
          aria-autocomplete="list"
        />
        {showSuggestions && (
          <ul
            id={suggestionsListId}
            className="inventory-suggestion-list"
            data-testid="inventory-name-suggestions"
            role="listbox"
          >
            {suggestions.map((suggestion) => (
              <li key={suggestion.id} role="option" aria-selected={false}>
                <button
                  type="button"
                  className="inventory-suggestion-item"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => onSelectSuggestion(suggestion)}
                >
                  <span className="inventory-suggestion-item-name">{suggestion.name}</span>
                  <span className="inventory-suggestion-item-meta">
                    {suggestion.brand ? `${suggestion.brand} • ` : ""}
                    ${suggestion.unit_price.toFixed(2)}/{suggestion.measure_unit}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </InventoryFormField>

    <InventoryFormField label="Description" error={errors.item_description?.message as string | undefined}>
      <textarea
        {...register("item_description")}
        placeholder="Optional description for this item"
        rows={3}
        maxLength={INVENTORY_ITEM_DESCRIPTION_MAX_LENGTH}
        className="inventory-textarea"
        data-testid="inventory-item-description"
        aria-describedby="inventory-item-description-count"
      />
      <p
        id="inventory-item-description-count"
        className="inventory-field-hint"
        data-testid="inventory-item-description-count"
        aria-live="polite"
      >
        {(watchedItemDescription?.length ?? 0)}/{INVENTORY_ITEM_DESCRIPTION_MAX_LENGTH}
      </p>
    </InventoryFormField>

    <InventoryFormField label="Unit price" error={errors.unit_price?.message as string | undefined}>
      <div className="inventory-form-field-controls">
        <input
          {...register("unit_price", { valueAsNumber: true })}
          type="number"
          step="0.01"
          min="0"
          className="inventory-input"
        />
        <PriceRecommendationNote
          detail={productRecommendation}
          displayUnit={watchedMeasureUnit || "unit"}
          category={categoryLabel}
          selectedSuggestion={selectedSuggestion}
        />
      </div>
    </InventoryFormField>

    <div className="inventory-form-field-group">
      <InventoryFormField label={stockQuantityLabel} error={errors.total_quantity?.message as string | undefined}>
        <input
          {...register("total_quantity", { valueAsNumber: true })}
          type="number"
          step="1"
          min="0"
          className="inventory-input"
        />
      </InventoryFormField>

      <InventoryFormField label="Measurement unit" error={errors.measure_unit?.message as string | undefined}>
        <select {...register("measure_unit")} className="inventory-select">
          <option value="" disabled>
            -- Unit --
          </option>
          {Object.entries(MeasurementUnits).map(([unit_enum_key, unit_enum_value], i) => (
            <option key={i} value={unit_enum_key}>
              {capitalizeString(unit_enum_value.toLowerCase())} ({unit_enum_key})
            </option>
          ))}
        </select>
      </InventoryFormField>
    </div>

    <InventoryFormField label="Usage type">
      <div className="inventory-reusable-field">
        <div className="min-w-0 flex-1">
          <p className="inventory-reusable-field-title">
            {isReusable ? "Reusable" : "Single use"}
          </p>
          <p className="inventory-reusable-field-description">
            {isReusable
              ? "This item can be booked and used repeatedly."
              : "This item is consumed or depleted when used."}
          </p>
        </div>
        <label className="inventory-switch">
          <input
            type="checkbox"
            checked={isReusable}
            onChange={(event) => onIsReusableChange(event.target.checked)}
            className="inventory-switch-input"
            data-testid="inventory-is-reusable"
            aria-label={isReusable ? "Reusable item" : "Single use item"}
          />
          <span
            className={`inventory-switch-control${isReusable ? " inventory-switch-control-on" : ""}`}
            aria-hidden="true"
          />
        </label>
      </div>
    </InventoryFormField>

    <InventoryFormField label="Item image" error={errors.image_url?.message as string | undefined}>
      <InventoryProductImageInput
        name="image_url"
        selectedImage={selectedProductImage}
        setSelectedImage={setSelectedProductImage}
        handleChange={handleInputChange}
        onClearImage={onClearImage}
      />
    </InventoryFormField>

    <div className="inventory-track-usage-panel" data-testid="inventory-track-usage-panel">
      <label className="inventory-track-usage-toggle">
        <div className="min-w-0 flex-1">
          <p className="inventory-track-usage-title">Track Usage</p>
          <p className="inventory-track-usage-description">
            Track operational usage and cost for this item during bookings
          </p>
        </div>
        <span className="inventory-switch">
          <input
            type="checkbox"
            checked={trackUsage}
            onChange={(event) => onTrackUsageChange(event.target.checked)}
            className="inventory-switch-input"
            data-testid="inventory-track-usage"
            aria-label="Track usage"
          />
          <span
            className={`inventory-switch-control${trackUsage ? " inventory-switch-control-on" : ""}`}
            aria-hidden="true"
          />
        </span>
      </label>

      {/* {trackUsage && (
        <div className="inventory-track-usage-fields" data-testid="inventory-track-usage-fields">
          {usageProfile === "space_storage" && (
            <InventoryFormField label="Space allocation">
              <select
                {...register("space_allocation")}
                value={spaceAllocation}
                onChange={(event) => onSpaceAllocationChange(event.target.value as SpaceAllocation)}
                className="inventory-select"
                data-testid="inventory-space-allocation"
              >
                {SPACE_ALLOCATION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="inventory-space-allocation-note">
                <span className="inventory-space-allocation-badge">
                  {spaceAllocation === "dedicated" ? "Dedicated" : "Shared"}
                </span>
                <span>{getSpaceAllocationDescription(spaceAllocation)}</span>
              </p>
            </InventoryFormField>
          )}

          <InventoryFormField
            label="Usage measurement unit"
            error={errors.usage_measure_unit?.message as string | undefined}
          >
            <select
              {...register("usage_measure_unit")}
              value={usageMeasureUnit}
              onChange={(event) => onUsageMeasureUnitChange(event.target.value as UsageMeasurementUnit)}
              className="inventory-select"
              data-testid="inventory-usage-measure-unit"
            >
              {usageUnitOptions.map((unit) => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </InventoryFormField>

          <InventoryFormField label="Unit cost ($)" error={errors.usage_unit_cost?.message as string | undefined}>
            <div className="inventory-form-field-controls">
              <input
                {...register("usage_unit_cost", { valueAsNumber: true })}
                type="number"
                min={0}
                step="0.00001"
                className="inventory-input"
                data-testid="inventory-usage-unit-price"
              />
              {usageRecommendation && (
                <UsagePriceRecommendationNote
                  detail={usageRecommendation}
                  usageUnit={(usageMeasureUnit || "hour") as UsageMeasurementUnit}
                  category={categoryLabel}
                />
              )}
            </div>
          </InventoryFormField>
        </div>
      )} */}
    </div>
    </div>
  );
};

type InventoryProductFormFooterProps = {
  isEdit: boolean;
  isModal?: boolean;
  onCancel: () => void;
  onSubmit: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

const InventoryProductFormFooter: React.FC<InventoryProductFormFooterProps> = ({
  isEdit,
  isModal = false,
  onCancel,
  onSubmit,
}) => (
  <div
    className={isModal ? "inventory-form-footer-modal" : "inventory-form-footer"}
    data-testid="inventory-product-form-footer"
  >
    <div className='w-11/12 mx-auto flex justify-between flex-row gap-x-4'>
    <button
      type="button"
      className="inventory-btn-outline"
      onClick={onCancel}
      data-testid="inventory-form-cancel"
    >
      Cancel
    </button>
    <button
      type="button"
      className="inventory-btn-primary"
      onClick={onSubmit}
      data-testid="inventory-form-submit"
    >
      {isEdit ? "Update Item" : "Add Item"}
    </button>
    </div>
  </div>
);

const addInventoryDefaultValues = {
  item_name: "",
  total_quantity: 1,
  category: 'equipment',
  measure_unit: 'kg',
  unit_price: 0,
  is_reusable: false,
  track_usage: false,
  // usage_measure_unit: 'hour' as UsageMeasurementUnit,
  // usage_unit_cost: 0,
  // space_allocation: 'dedicated' as SpaceAllocation,
  image_url: undefined,
  item_description: "",
};

const AddInventoryProductForm: React.FC<AddProductFormProps> = ({ resourceCategory, resourceId, isModal, isOpen, setIsAddingProduct, setCurrentInventoryList, resourceType }) => {
  const form = useForm<z.infer<typeof AddInventorySchema>>({
    resolver: zodResolver(AddInventorySchema),
    defaultValues: addInventoryDefaultValues,
  });
  const { register, handleSubmit, setValue, getValues, watch, formState, reset } = form;
  const { addInventoryItem, isError } = useResourceHook({ resourceId });
  const { currentResource } = useLoadCurrentResource(resourceId || "");
  const [selectedProductImage, setSelectedProductImage] = useState<string>("");
  const { errors } = formState;

  const watchedCategory = watch("category");
  const watchedItemName = watch("item_name");
  const watchedMeasureUnit = watch("measure_unit");
  const watchedIsReusable = watch("is_reusable");
  const watchedTrackUsage = watch("track_usage");
  // const watchedUsageMeasureUnit = watch("usage_measure_unit");
  // const watchedSpaceAllocation = watch("space_allocation");
  const watchedItemDescription = watch("item_description");

  const {
    suggestions,
    selectedSuggestion,
    handleItemNameChange,
    handleCategoryChange,
    clearSuggestions,
    selectSuggestion,
  } = useInventoryNameSuggestions(watchedCategory);

  const productRecommendation = React.useMemo(
    () => getProductPriceRecommendationDetail(currentResource?.address, watchedCategory, watchedItemName),
    [currentResource?.address, watchedCategory, watchedItemName]
  );

  // const usageRecommendation = React.useMemo(
  //   () =>
  //     watchedTrackUsage
  //       ? getUsagePriceRecommendationDetail(
  //           currentResource?.address,
  //           watchedCategory,
  //           (watchedUsageMeasureUnit || getDefaultUsageMeasureUnit(watchedCategory)) as UsageMeasurementUnit
  //         )
  //       : null,
  //   [currentResource?.address, watchedCategory, watchedTrackUsage, watchedUsageMeasureUnit]
  // );

  const resetInventoryForm = React.useCallback(() => {
    reset(addInventoryDefaultValues);
    setSelectedProductImage("");
    clearSuggestions();
  }, [reset, clearSuggestions]);

  React.useEffect(() => {
    if (isModal) {
      if (isOpen) {
        resetInventoryForm();
      }
    } else {
      resetInventoryForm();
    }
  }, [isModal, isOpen, resetInventoryForm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, files }: any = e.target;
    if (name === "image_url" && files?.[0]) {
      setValue("image_url", files[0]);
    }
  };

  const handleSelectSuggestion = (suggestion: InventoryCatalogSuggestion) => {
    setValue("item_name", suggestion.name);
    setValue("unit_price", suggestion.unit_price);
    setValue("measure_unit", suggestion.measure_unit);
    selectSuggestion(suggestion);
  };

  const handleClearImage = () => {
    setValue("image_url", undefined);
  };

  const handleCategoryFieldChange = () => {
    const itemName = getValues("item_name") || "";
    handleCategoryChange(itemName);
    // const nextUsageUnit = getDefaultUsageMeasureUnit(getValues("category") || "");
    // setValue("usage_measure_unit", nextUsageUnit, { shouldDirty: true });
    // if (getValues("track_usage")) {
    //   setValue(
    //     "usage_unit_cost",
    //     getUsagePriceRecommendationDetail(currentResource?.address, getValues("category") || "", nextUsageUnit).price,
    //     { shouldDirty: true }
    //   );
    // }
  };

  const handleTrackUsageChange = (enabled: boolean) => {
    setValue("track_usage", enabled, { shouldDirty: true });
    // if (enabled) {
    //   const unit = getDefaultUsageMeasureUnit(getValues("category") || "");
    //   setValue("usage_measure_unit", unit, { shouldDirty: true });
    //   setValue(
    //     "usage_unit_cost",
    //     getUsagePriceRecommendationDetail(currentResource?.address, getValues("category") || "", unit).price,
    //     { shouldDirty: true }
    //   );
    // }
  };

  // const handleUsageMeasureUnitChange = (unit: UsageMeasurementUnit) => {
  //   setValue("usage_measure_unit", unit, { shouldDirty: true });
  //   setValue(
  //     "usage_unit_cost",
  //     getUsagePriceRecommendationDetail(currentResource?.address, getValues("category") || "", unit).price,
  //     { shouldDirty: true }
  //   );
  // };

  const onSubmit = (data: FieldValues) => {
    const productData = { ...data, resource_type: resourceType } as InventorySchema;

    // if (!productData.track_usage) {
    //   delete productData.usage_measure_unit;
    //   delete productData.usage_unit_cost;
    //   delete productData.space_allocation;
    // } else if (getUsageTrackProfile(productData.category) !== "space_storage") {
    //   delete productData.space_allocation;
    // }
    
    // If resourceId exists, add via API (for editing existing resource)
    if (resourceId) {
      addInventoryItem(resourceId, productData);
      if(isError){
        toast.error("Failed to add product. Please try again.");
        return;
      } else {
        toast.success("Product added successfully!");
      }
    } else {
      // If no resourceId, add to local state (for new resource being created)
      if (setCurrentInventoryList) {
        setCurrentInventoryList((prev: any) => [...prev, productData]);
        toast.success("Product added successfully!");
      }
    }
    resetInventoryForm();
    setIsAddingProduct(false);
  };

  return (
    <div
      data-testid="inventory-product-form"
      className={isModal ? "inventory-form-shell" : undefined}
    >
      {!isModal && (
        <button
          type="button"
          className="mb-3 border-0 font-raleway text-sm text-primary-300 hover:text-primary-200"
          onClick={() => setIsAddingProduct(false)}
        >
          Back
        </button>
      )}

      <InventoryProductFormHeader
        isEdit={false}
        isModal={isModal}
        onClose={isModal ? () => setIsAddingProduct(false) : undefined}
        resourceType={resourceType}
      />

      <div className={isModal ? "inventory-form-body" : undefined}>
        <InventoryProductFormFields
          register={register}
          errors={errors}
          handleInputChange={handleInputChange}
          onItemNameChange={handleItemNameChange}
          onCategoryChange={handleCategoryFieldChange}
          suggestions={suggestions}
          onSelectSuggestion={handleSelectSuggestion}
          selectedProductImage={selectedProductImage}
          setSelectedProductImage={setSelectedProductImage}
          onClearImage={handleClearImage}
          productRecommendation={productRecommendation}
          // usageRecommendation={usageRecommendation}
          watchedCategory={watchedCategory}
          watchedMeasureUnit={watchedMeasureUnit}
          selectedSuggestion={selectedSuggestion}
          isReusable={Boolean(watchedIsReusable)}
          onIsReusableChange={(value) => setValue("is_reusable", value, { shouldDirty: true })}
          trackUsage={Boolean(watchedTrackUsage)}
          onTrackUsageChange={handleTrackUsageChange}
          // usageMeasureUnit={watchedUsageMeasureUnit || getDefaultUsageMeasureUnit(watchedCategory)}
          // onUsageMeasureUnitChange={handleUsageMeasureUnitChange}
          // spaceAllocation={(watchedSpaceAllocation || "dedicated") as SpaceAllocation}
          // onSpaceAllocationChange={(value) => setValue("space_allocation", value, { shouldDirty: true })}
          watchedItemDescription={watchedItemDescription}
        />
      </div>

      <InventoryProductFormFooter
        isEdit={false}
        isModal={isModal}
        onCancel={() => setIsAddingProduct(false)}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
      />
    </div>
  );
};


const UpdateInventoryProductForm: React.FC<UpdateProductFormProps> = ({ resourceCategory, resourceId, isModal, setIsAddingProduct, setCurrentInventoryList, resourceType, itemId }) => {
  const { currentResource } = useLoadCurrentResource(resourceId || "");
  const [originalItem, setOriginalItem] = useState<InventorySchema | null>(null);
  const [selectedProductImage, setSelectedProductImage] = useState<string>("");
  
  const form = useForm<z.infer<typeof AddInventorySchema>>({
    resolver: zodResolver(AddInventorySchema),
    defaultValues: addInventoryDefaultValues,
  });
  const { register, handleSubmit, setValue, getValues, watch, formState, reset } = form;
  const { updateInventoryItemById } = useResourceHook({ resourceId });
  const { errors } = formState;

  const watchedCategory = watch("category");
  const watchedItemName = watch("item_name");
  const watchedMeasureUnit = watch("measure_unit");
  const watchedIsReusable = watch("is_reusable");
  const watchedTrackUsage = watch("track_usage");
  // const watchedUsageMeasureUnit = watch("usage_measure_unit");
  // const watchedSpaceAllocation = watch("space_allocation");
  const watchedItemDescription = watch("item_description");

  const {
    suggestions,
    selectedSuggestion,
    handleItemNameChange,
    handleCategoryChange,
    // clearSuggestions,
    selectSuggestion,
  } = useInventoryNameSuggestions(watchedCategory);

  const productRecommendation = React.useMemo(
    () => getProductPriceRecommendationDetail(currentResource?.address, watchedCategory, watchedItemName),
    [currentResource?.address, watchedCategory, watchedItemName]
  );

  // const usageRecommendation = React.useMemo(
  //   () =>
  //     watchedTrackUsage
  //       ? getUsagePriceRecommendationDetail(
  //           currentResource?.address,
  //           watchedCategory,
  //           (watchedUsageMeasureUnit || getDefaultUsageMeasureUnit(watchedCategory)) as UsageMeasurementUnit
  //         )
  //       : null,
  //   [currentResource?.address, watchedCategory, watchedTrackUsage, watchedUsageMeasureUnit]
  // );

  // Find the existing item from resource inventory
  React.useEffect(() => {
    if (currentResource && itemId && (!originalItem || originalItem.id !== itemId)) {
      const resourceInventory = currentResource?.resource_inventory as ResourceInventorySchema;
      const items = resourceInventory?.items as InventorySchema[];
      const existingItem = items?.find(item => item.id === itemId);
      if (existingItem) {
        setOriginalItem(existingItem);
        // Populate form with existing data
        reset({
          item_name: existingItem.item_name || "",
          item_description: existingItem.item_description || "",
          category: existingItem.category || 'equipment',
          measure_unit: existingItem.measure_unit || 'kg',
          unit_price: existingItem.unit_price || 0,
          total_quantity: existingItem.total_quantity || 1,
          is_reusable: existingItem.is_reusable ?? false,
          track_usage: existingItem.track_usage ?? false,
          // usage_measure_unit:
            // existingItem.usage_measure_unit ||
            // getDefaultUsageMeasureUnit(existingItem.category || "equipment"),
            // usage_unit_cost: existingItem.usage_unit_cost ?? 0,
          // space_allocation: existingItem.space_allocation ?? "dedicated",
          image_url: undefined, // Don't pre-populate file input
        });
        if (existingItem.image_url) {
          setSelectedProductImage(existingItem.image_url);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentResource, itemId]); // Removed reset and originalItem from dependencies to prevent infinite loops

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, files }: any = e.target;
    if (name === "image_url" && files?.[0]) {
      setValue("image_url", files[0]);
    }
  };

  const handleSelectSuggestion = (suggestion: InventoryCatalogSuggestion) => {
    setValue("item_name", suggestion.name);
    setValue("unit_price", suggestion.unit_price);
    setValue("measure_unit", suggestion.measure_unit);
    selectSuggestion(suggestion);
  };

  const handleClearImage = () => {
    setValue("image_url", undefined);
  };

  const handleCategoryFieldChange = () => {
    const itemName = getValues("item_name") || "";
    handleCategoryChange(itemName);
    // const nextUsageUnit = getDefaultUsageMeasureUnit(getValues("category") || "");
    // setValue("usage_measure_unit", nextUsageUnit, { shouldDirty: true });
    // if (getValues("track_usage")) {
    //   setValue(
    //     "usage_unit_cost",
    //     getUsagePriceRecommendationDetail(currentResource?.address, getValues("category") || "", nextUsageUnit).price,
    //     { shouldDirty: true }
    //   );
    // }
  };

  const handleTrackUsageChange = (enabled: boolean) => {
    setValue("track_usage", enabled, { shouldDirty: true });
    // if (enabled) {
    //   const unit = getDefaultUsageMeasureUnit(getValues("category") || "");
    //   setValue("usage_measure_unit", unit, { shouldDirty: true });
    //   setValue(
    //     "usage_unit_cost",
    //     getUsagePriceRecommendationDetail(currentResource?.address, getValues("category") || "", unit).price,
    //     { shouldDirty: true }
    //   );
    // }
  };

  // const handleUsageMeasureUnitChange = (unit: UsageMeasurementUnit) => {
  //   setValue("usage_measure_unit", unit, { shouldDirty: true });
  //   setValue(
  //     "usage_unit_cost",
  //     getUsagePriceRecommendationDetail(currentResource?.address, getValues("category") || "", unit).price,
  //     { shouldDirty: true }
  //   );
  // };

  const onSubmit = async (data: FieldValues) => {
    const productData = { ...data, resource_type: resourceType } as InventorySchema;

    // if (!productData.track_usage) {
    //   delete productData.usage_measure_unit;
    //   delete productData.usage_unit_cost;
    //   delete productData.space_allocation;
    // } else if (getUsageTrackProfile(productData.category) !== "space_storage") {
    //   delete productData.space_allocation;
    // }
    
    // If resourceId and itemId exist, update via API
    if (resourceId && itemId && originalItem) {
      // Use getChangedAttributes to only send changed fields
      const changedAttributes = getChangedAttributes(originalItem, productData);
      
      if (Object.keys(changedAttributes).length > 0) {
        try {
          const result = await updateInventoryItemById(resourceId, itemId, changedAttributes as InventorySchema);
          if (result) {
            toast.success("Product updated successfully!");
            setIsAddingProduct(false);
          } else {
            toast.error("Failed to update product. Please try again.");
          }
        } catch (error) {
          console.error("Error updating product:", error);
          toast.error("Failed to update product. Please try again.");
        }
      } else {
        toast.info("No changes detected.");
        setIsAddingProduct(false);
      }
    } else {
      toast.error("Missing required information to update product.");
    }
  };

  return (
    <div
      data-testid="inventory-product-form"
      className={isModal ? "inventory-form-shell" : undefined}
    >
      {!isModal && (
        <button
          type="button"
          className="mb-3 border-0 font-raleway text-sm text-primary-300 hover:text-primary-200"
          onClick={() => setIsAddingProduct(false)}
        >
          Back
        </button>
      )}

      <InventoryProductFormHeader
        isEdit
        isModal={isModal}
        onClose={isModal ? () => setIsAddingProduct(false) : undefined}
        resourceType={resourceType}
      />

      <div className={isModal ? "inventory-form-body" : undefined}>
        <InventoryProductFormFields
          register={register}
          errors={errors}
          handleInputChange={handleInputChange}
          onItemNameChange={handleItemNameChange}
          onCategoryChange={handleCategoryFieldChange}
          suggestions={suggestions}
          onSelectSuggestion={handleSelectSuggestion}
          selectedProductImage={selectedProductImage}
          setSelectedProductImage={setSelectedProductImage}
          onClearImage={handleClearImage}
          productRecommendation={productRecommendation}
          // usageRecommendation={usageRecommendation}
          watchedCategory={watchedCategory}
          watchedMeasureUnit={watchedMeasureUnit}
          selectedSuggestion={selectedSuggestion}
          isReusable={Boolean(watchedIsReusable)}
          onIsReusableChange={(value) => setValue("is_reusable", value, { shouldDirty: true })}
          trackUsage={Boolean(watchedTrackUsage)}
          onTrackUsageChange={handleTrackUsageChange}
          // usageMeasureUnit={watchedUsageMeasureUnit || getDefaultUsageMeasureUnit(watchedCategory)}
          // onUsageMeasureUnitChange={handleUsageMeasureUnitChange}
          // spaceAllocation={(watchedSpaceAllocation || "dedicated") as SpaceAllocation}
          // onSpaceAllocationChange={(value) => setValue("space_allocation", value, { shouldDirty: true })}
          watchedItemDescription={watchedItemDescription}
        />
      </div>

      <InventoryProductFormFooter
        isEdit
        isModal={isModal}
        onCancel={() => setIsAddingProduct(false)}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
      />
    </div>
  );
};

// --- Wrapper ---
export const AddProductForm: React.FC<AddProductFormProps> = (props) => {
  const isTraining = ["training", "expert"].includes(props.resourceType);
  return isTraining ? (
    <AddTrainingProductForm {...props} />
  ) : (
    <AddInventoryProductForm {...props} />
  );
};


// --- Wrapper ---
export const UpdateProductForm: React.FC<UpdateProductFormProps> = (props) => {
  const isTraining = ["training", "expert"].includes(props.resourceType);
  return isTraining ? (
    <UpdateTrainingProductForm {...props} />
  ) : (
    <UpdateInventoryProductForm {...props} />
  );
};